import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, centAusText, errorText, formatDate, formatDateTime, formatGeld } from '../api';
import { useAuth } from '../auth';
import { Unterschrift, kartenLink, zeitfenster } from '../components/Einsatz';
import { EinsatzFotos } from '../components/Fotos';
import { PositionenEditor, leerePosition, positionenFuerApi, type PositionEingabe } from '../components/Vorgang';
import { Badge, Card, Dialog, Field, Loading, Notice, kundenName } from '../components/ui';
import type { Einsatz, Protokoll } from '../types';

type JaNein = boolean | null;

/** Ja/Nein ohne Vorgabe - eine Antwort muss man aktiv geben. */
function Frage({ nr, frage, wert, onChange, children }: { nr: number; frage: string; wert: JaNein; onChange: (w: boolean) => void; children?: ReactNode }) {
  return (
    <div className={wert === null ? 'frage offen' : 'frage'}>
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <strong style={{ flex: 1 }}>
          {nr}. {frage}
        </strong>
        <div className="janein">
          <button type="button" className={wert === true ? 'aktiv' : ''} onClick={() => onChange(true)}>
            Ja
          </button>
          <button type="button" className={wert === false ? 'aktiv' : ''} onClick={() => onChange(false)}>
            Nein
          </button>
        </div>
      </div>
      {children}
    </div>
  );
}

function ProtokollFormular({ e, onFertig }: { e: Einsatz; onFertig: (e: Einsatz) => void }) {
  const [taetigkeiten, setTaetigkeiten] = useState('');
  const [arbeitszeit, setArbeitszeit] = useState('');
  const [berechnung, setBerechnung] = useState<JaNein>(null);
  const [positionen, setPositionen] = useState<PositionEingabe[]>([leerePosition()]);
  const [keineGrund, setKeineGrund] = useState('');
  const [zahlArt, setZahlArt] = useState<'' | 'bar' | 'ec'>('');
  const [zahlBetrag, setZahlBetrag] = useState('');
  const [nacharbeit, setNacharbeit] = useState<JaNein>(null);
  const [nacharbeitText, setNacharbeitText] = useState('');
  const [material, setMaterial] = useState<JaNein>(null);
  const [materialPos, setMaterialPos] = useState<PositionEingabe[]>([leerePosition()]);
  const [rueckruf, setRueckruf] = useState<JaNein>(null);
  const [rueckrufText, setRueckrufText] = useState('');
  const [folgetermin, setFolgetermin] = useState<JaNein>(null);
  const [anwesend, setAnwesend] = useState<JaNein>(null);
  const [png, setPng] = useState<string | null>(null);
  const [name, setName] = useState(kundenName({ vorname: e.kunde_vorname, nachname: e.kunde_nachname }));
  const [ohneGrund, setOhneGrund] = useState('');
  const [vorlagen, setVorlagen] = useState<{ leistungen: { bezeichnung: string; preis_cent: number }[]; taetigkeiten: string[] }>({ leistungen: [], taetigkeiten: [] });
  const [fehler, setFehler] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<typeof vorlagen>('/api/einstellungen/protokoll-vorlagen').then(setVorlagen);
  }, []);

  const summe = positionenFuerApi(positionen).reduce((s, p) => s + (p.preisCent ?? 0) * p.menge, 0);

  const leistungDazu = (l: { bezeichnung: string; preis_cent: number }) =>
    setPositionen((alt) => [
      ...alt.filter((p) => p.bezeichnung.trim()),
      { ...leerePosition(), bezeichnung: l.bezeichnung, preis: (l.preis_cent / 100).toFixed(2).replace('.', ',') },
    ]);

  // Was vor dem Absenden noch fehlt - dieselben Regeln wie im Server, nur früher sichtbar.
  const offen: string[] = [];
  if (taetigkeiten.trim().length < 3) offen.push('Tätigkeiten');
  if (berechnung === null || nacharbeit === null || material === null || rueckruf === null || folgetermin === null) offen.push('alle Pflichtfragen beantworten');
  if (berechnung && summe <= 0) offen.push('Positionen mit Preis');
  // Bargeld, das gewählt, aber nicht beziffert ist, ginge sonst still verloren.
  if (berechnung && zahlArt && !centAusText(zahlBetrag)) offen.push('kassierten Betrag');
  if (berechnung === false && keineGrund.trim().length < 3) offen.push('Grund für "nichts zu berechnen"');
  if (nacharbeit && nacharbeitText.trim().length < 3) offen.push('Nacharbeit beschreiben');
  if (material && positionenFuerApi(materialPos).length === 0) offen.push('Material angeben');
  if (rueckruf && rueckrufText.trim().length < 3) offen.push('Anlass für den Rückruf');
  if (anwesend === null) offen.push('Kunde anwesend?');
  if (!(png && name.trim().length >= 2) && ohneGrund.trim().length < 3) offen.push('Unterschrift oder Grund');

  async function absenden() {
    setBusy(true);
    setFehler([]);
    try {
      const zahlung = berechnung && zahlArt && centAusText(zahlBetrag) ? { art: zahlArt, betragCent: centAusText(zahlBetrag)! } : null;
      const r = await api<Einsatz>(`/api/einsaetze/${e.id}/protokoll`, {
        method: 'POST',
        body: {
          taetigkeiten,
          arbeitszeitMinuten: arbeitszeit ? Number(arbeitszeit) : null,
          berechnung,
          positionen: berechnung ? positionenFuerApi(positionen).map((p) => ({ bezeichnung: p.bezeichnung, menge: p.menge, preisCent: p.preisCent ?? 0 })) : undefined,
          keineBerechnungGrund: berechnung ? null : keineGrund,
          zahlungVorOrt: zahlung,
          nacharbeit,
          nacharbeitText: nacharbeit ? nacharbeitText : null,
          material,
          materialPositionen: material ? positionenFuerApi(materialPos).map((p) => ({ bezeichnung: p.bezeichnung, menge: p.menge })) : undefined,
          rueckruf,
          rueckrufText: rueckruf ? rueckrufText : null,
          folgetermin,
          kundeAnwesend: anwesend,
          unterschriftPng: png,
          unterschriftName: png ? name : null,
          ohneUnterschriftGrund: png ? null : ohneGrund,
        },
      });
      onFertig(r);
      window.scrollTo(0, 0);
    } catch (err) {
      const text = errorText(err);
      setFehler(text.includes(': ') ? text.split(': ').slice(1).join(': ').split(', ') : [text]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <Card title="Was wurde gemacht?">
        <div className="stack" style={{ gap: 10 }}>
          <div className="row">
            {vorlagen.taetigkeiten.map((t) => (
              <button key={t} type="button" className="klein" onClick={() => setTaetigkeiten((alt) => (alt ? `${alt.trimEnd()}\n${t}` : t))}>
                + {t.replace(/\.$/, '')}
              </button>
            ))}
          </div>
          <textarea value={taetigkeiten} onChange={(ev) => setTaetigkeiten(ev.target.value)} placeholder="Tätigkeiten (Pflicht)" />
          <Field label="Arbeitszeit (Minuten, optional)">
            <input value={arbeitszeit} onChange={(ev) => setArbeitszeit(ev.target.value.replace(/\D/g, ''))} inputMode="numeric" style={{ maxWidth: 160 }} />
          </Field>
        </div>
      </Card>

      <Card title="Pflichtfragen – jede muss beantwortet werden">
        <div className="stack" style={{ gap: 12 }}>
          <Frage nr={1} frage="Ist etwas zu berechnen?" wert={berechnung} onChange={setBerechnung}>
            {berechnung === true && (
              <div className="stack" style={{ gap: 8, marginTop: 8 }}>
                <div className="row">
                  {vorlagen.leistungen.map((l) => (
                    <button key={l.bezeichnung} type="button" className="klein" onClick={() => leistungDazu(l)}>
                      + {l.bezeichnung} ({formatGeld(l.preis_cent)})
                    </button>
                  ))}
                </div>
                <PositionenEditor positionen={positionen} onChange={setPositionen} />
                <div className="grid2">
                  <Field label="Vor Ort kassiert?">
                    <select
                      value={zahlArt}
                      onChange={(ev) => {
                        const art = ev.target.value as '' | 'bar' | 'ec';
                        setZahlArt(art);
                        // Meist wird der volle Betrag kassiert - vorbelegen, änderbar.
                        if (art && !zahlBetrag && summe > 0) setZahlBetrag((summe / 100).toFixed(2).replace('.', ','));
                        if (!art) setZahlBetrag('');
                      }}
                    >
                      <option value="">Nein – Rechnung folgt</option>
                      <option value="bar">Ja, bar</option>
                      <option value="ec">Ja, EC-Karte</option>
                    </select>
                  </Field>
                  {zahlArt && (
                    <Field label="Kassierter Betrag €" hint={summe > 0 ? `berechnet: ${formatGeld(summe)}` : undefined}>
                      <input value={zahlBetrag} onChange={(ev) => setZahlBetrag(ev.target.value)} inputMode="decimal" />
                    </Field>
                  )}
                </div>
                <div className="small muted">Es entsteht eine Forderung. Der Laden trägt den MehrData-Beleg ein; erst dann und mit voller Zahlung ist sie erledigt.</div>
              </div>
            )}
            {berechnung === false && (
              <input style={{ marginTop: 8 }} placeholder="Warum nicht? z. B. im Kaufpreis enthalten, Garantie (Pflicht)" value={keineGrund} onChange={(ev) => setKeineGrund(ev.target.value)} />
            )}
          </Frage>
          <Frage nr={2} frage="Ist Nacharbeit nötig?" wert={nacharbeit} onChange={setNacharbeit}>
            {nacharbeit && <textarea style={{ marginTop: 8, minHeight: 50 }} placeholder="Was ist noch zu tun? (wird ein Ticket für dich)" value={nacharbeitText} onChange={(ev) => setNacharbeitText(ev.target.value)} />}
          </Frage>
          <Frage nr={3} frage="Muss Material bestellt werden?" wert={material} onChange={setMaterial}>
            {material && (
              <div style={{ marginTop: 8 }}>
                <PositionenEditor positionen={materialPos} onChange={setMaterialPos} mitPreis={false} />
                <div className="small muted">Wird als Bestellung im Laden angelegt.</div>
              </div>
            )}
          </Frage>
          <Frage nr={4} frage="Soll der Kunde zurückgerufen werden?" wert={rueckruf} onChange={setRueckruf}>
            {rueckruf && <input style={{ marginTop: 8 }} placeholder="Worum geht es? (Rückruf durch den Laden binnen 24 Std.)" value={rueckrufText} onChange={(ev) => setRueckrufText(ev.target.value)} />}
          </Frage>
          <Frage nr={5} frage="Ist ein weiterer Termin nötig?" wert={folgetermin} onChange={setFolgetermin}>
            {folgetermin && <div className="small muted" style={{ marginTop: 6 }}>Der Vorgang bleibt offen und geht zurück auf „Termin vereinbaren".</div>}
          </Frage>
        </div>
      </Card>

      <Card title="Abnahme">
        <div className="stack" style={{ gap: 10 }}>
          <Frage nr={6} frage="War der Kunde anwesend?" wert={anwesend} onChange={setAnwesend} />
          <Field label="Name des Unterschreibenden">
            <input value={name} onChange={(ev) => setName(ev.target.value)} />
          </Field>
          <Unterschrift onChange={setPng} />
          {!png && (
            <Field label="Keine Unterschrift möglich? Grund">
              <input value={ohneGrund} onChange={(ev) => setOhneGrund(ev.target.value)} />
            </Field>
          )}
        </div>
      </Card>

      {fehler.length > 0 && (
        <Notice kind="error">
          {fehler.map((f) => (
            <div key={f}>{f}</div>
          ))}
        </Notice>
      )}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        {offen.length > 0 && <span className="small muted">Fehlt noch: {offen.join(', ')}</span>}
        <button className="primary" disabled={busy || offen.length > 0} onClick={() => void absenden()}>
          Protokoll abschließen
        </button>
      </div>
    </div>
  );
}

function ProtokollAnsicht({ p }: { p: Protokoll }) {
  const ja = (b: boolean) => (b ? <Badge kind="info">Ja</Badge> : <Badge>Nein</Badge>);
  return (
    <Card title={`Protokoll vom ${formatDateTime(p.erstellt_am)} · ${p.erstellt_von_name}`}>
      <dl className="daten">
        <dt>Tätigkeiten</dt>
        <dd style={{ whiteSpace: 'pre-wrap' }}>{p.taetigkeiten}</dd>
        {p.arbeitszeit_minuten != null && (
          <>
            <dt>Arbeitszeit</dt>
            <dd>{p.arbeitszeit_minuten} Min.</dd>
          </>
        )}
        <dt>Berechnung</dt>
        <dd>
          {ja(p.frage_berechnung)}{' '}
          {p.forderung_id ? (
            <Link to={`/forderungen/${p.forderung_id}`}>
              Forderung {p.forderung_nummer} über {formatGeld(p.forderung_betrag_cent)}
            </Link>
          ) : (
            p.keine_berechnung_grund
          )}
        </dd>
        <dt>Nacharbeit</dt>
        <dd>
          {ja(p.frage_nacharbeit)} {p.nacharbeit_ticket_id && <Link to={`/tickets/${p.nacharbeit_ticket_id}`}>Ticket {p.nacharbeit_ticket_nummer}</Link>}
        </dd>
        <dt>Material</dt>
        <dd>
          {ja(p.frage_material)} {p.material_bestellung_id && <Link to={`/bestellungen/${p.material_bestellung_id}`}>Bestellung {p.material_bestellung_nummer}</Link>}
        </dd>
        <dt>Rückruf</dt>
        <dd>
          {ja(p.frage_rueckruf)} {p.rueckruf_ticket_id && <Link to={`/tickets/${p.rueckruf_ticket_id}`}>Ticket {p.rueckruf_ticket_nummer}</Link>}
        </dd>
        <dt>Folgetermin</dt>
        <dd>{ja(p.frage_folgetermin)}</dd>
        <dt>Kunde anwesend</dt>
        <dd>{ja(p.kunde_anwesend)}</dd>
        <dt>Unterschrift</dt>
        <dd>
          {p.unterschrift_png ? (
            <>
              <img src={p.unterschrift_png} alt="Unterschrift" style={{ maxWidth: 320, background: '#fff', border: '1px solid var(--line)', borderRadius: 6 }} />
              <div className="small">{p.unterschrift_name}</div>
            </>
          ) : (
            <span className="muted">keine – {p.ohne_unterschrift_grund}</span>
          )}
        </dd>
      </dl>
    </Card>
  );
}

export function EinsatzDetailPage() {
  const { id } = useParams();
  const { can, user } = useAuth();
  const [e, setE] = useState<Einsatz | null>(null);
  const [fehler, setFehler] = useState('');
  const [abbrechen, setAbbrechen] = useState(false);
  const [grund, setGrund] = useState('');

  const laden = useCallback(() => {
    api<Einsatz>(`/api/einsaetze/${id}`)
      .then(setE)
      .catch((err) => setFehler(errorText(err)));
  }, [id]);
  useEffect(laden, [laden]);

  if (!e) return fehler ? <Notice kind="error">{fehler}</Notice> : <Loading />;
  const eingeteilt = e.mitarbeiter.some((m) => m.id === user?.id);
  const darfProtokoll = e.status === 'geplant' && (eingeteilt || can('admin', 'verkauf'));
  const bezug = e.lieferung_id ? { pfad: `/lieferungen/${e.lieferung_id}`, nummer: e.lieferung_nummer } : { pfad: `/service/${e.serviceauftrag_id}`, nummer: e.serviceauftrag_nummer };

  async function abbruch() {
    try {
      setE(await api<Einsatz>(`/api/einsaetze/${e!.id}/abbrechen`, { method: 'POST', body: { grund } }));
      setAbbrechen(false);
    } catch (err) {
      setFehler(errorText(err));
    }
  }

  return (
    <>
      <div className="kopf">
        <div>
          <div className="muted small">
            <Link to="/einsaetze">Einsätze</Link> / <span className="mono">{e.nummer}</span> ·{' '}
            {can('admin', 'verkauf') || e.lieferung_id ? <Link to={bezug.pfad}>{bezug.nummer}</Link> : bezug.nummer}
          </div>
          <h1>
            {e.art === 'lieferung' ? '🚚' : '🔧'} {kundenName({ vorname: e.kunde_vorname, nachname: e.kunde_nachname, firma: e.kunde_firma })}
          </h1>
        </div>
        <span className="spacer" />
        <Badge kind={e.status === 'erledigt' ? 'ok' : e.status === 'abgebrochen' ? 'warn' : 'info'}>{e.status}</Badge>
      </div>
      {fehler && <Notice kind="error">{fehler}</Notice>}

      <div className="spalten haupt-rechts" style={{ marginBottom: 16 }}>
        <Card title="Termin">
          <dl className="daten">
            <dt>Wann</dt>
            <dd>
              {formatDate(e.datum)}, {zeitfenster(e.zeit_von, e.zeit_bis)}
            </dd>
            <dt>Wo</dt>
            <dd>
              <a href={kartenLink(e.strasse, e.plz, e.ort)} target="_blank" rel="noreferrer">
                {[e.strasse, [e.plz, e.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')} ↗
              </a>
            </dd>
            <dt>Telefon</dt>
            <dd>
              {(e.kunde_telefone ?? []).map((t) => (
                <a key={t} href={`tel:${t.replace(/[^\d+]/g, '')}`} style={{ marginRight: 10 }}>
                  ☎ {t}
                </a>
              ))}
            </dd>
            <dt>Team</dt>
            <dd>{e.mitarbeiter.map((m) => m.name).join(', ')}</dd>
            {e.abbruch_grund && (
              <>
                <dt>Abgebrochen</dt>
                <dd>{e.abbruch_grund}</dd>
              </>
            )}
          </dl>
        </Card>
        <Card title={e.art === 'lieferung' ? 'Was wird geliefert' : 'Gerät und Fehler'}>
          <dl className="daten">
            {e.art === 'lieferung' ? (
              <>
                <dt>Artikel</dt>
                <dd>{e.artikel}</dd>
                <dt>Leistungen</dt>
                <dd>{(e.leistungen ?? []).join(', ') || '—'}</dd>
              </>
            ) : (
              <>
                <dt>Gerät</dt>
                <dd>{e.geraet}</dd>
                <dt>Fehler</dt>
                <dd style={{ whiteSpace: 'pre-wrap' }}>{e.fehlerbeschreibung}</dd>
              </>
            )}
            {(e.hinweis || e.lieferhinweis) && (
              <>
                <dt>Hinweis</dt>
                <dd>
                  <strong>{[e.lieferhinweis, e.hinweis].filter(Boolean).join(' · ')}</strong>
                </dd>
              </>
            )}
          </dl>
        </Card>
      </div>

      <div style={{ marginBottom: 16 }}>
        <EinsatzFotos einsatzId={e.id} />
      </div>
      {e.protokoll ? (
        <ProtokollAnsicht p={e.protokoll} />
      ) : darfProtokoll ? (
        <>
          <ProtokollFormular e={e} onFertig={setE} />
          <div style={{ marginTop: 16 }}>
            <button className="gefahr" onClick={() => setAbbrechen(true)}>
              Einsatz nicht durchführbar …
            </button>
          </div>
        </>
      ) : null}

      {abbrechen && (
        <Dialog
          titel="Einsatz nicht durchführbar"
          onClose={() => setAbbrechen(false)}
          aktionen={
            <>
              <button onClick={() => setAbbrechen(false)}>Zurück</button>
              <button className="gefahr" disabled={grund.trim().length < 3} onClick={() => void abbruch()}>
                Einsatz abbrechen
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>Der Vorgang geht zurück auf „Termin vereinbaren" und erscheint beim Laden zur Neuplanung.</p>
          <Field label="Grund (Pflicht)">
            <input value={grund} onChange={(ev) => setGrund(ev.target.value)} placeholder="z. B. Kunde nicht angetroffen" autoFocus />
          </Field>
        </Dialog>
      )}
    </>
  );
}
